/**
 * App header component.
 *
 * Route-aware with three modes:
 * - Dashboard (/): Satsflow branding
 * - Tab pages (Activity, Contacts, Settings): centered page title
 * - Sub-pages: back button with page title
 *
 * Tier 1 (beginner) — always visible.
 */

import { useLocation, useNavigate } from 'react-router';
import { usePageTitle } from '@/contexts/PageTitleContext';

/** Tab pages that handle their own titles — Header is hidden on these routes. */
const HIDDEN_HEADER_ROUTES: ReadonlySet<string> = new Set(['/history', '/contacts', '/settings']);

/** Map of sub-page paths to their display title and parent route. */
const SUB_PAGE_CONFIG: Record<string, { title: string; parent: string }> = {
  '/send': { title: 'Send', parent: '/' },
  '/receive': { title: 'Receive', parent: '/' },
  '/settings/security': { title: 'Security', parent: '/settings' },
  '/settings/mints': { title: 'Mints', parent: '/settings' },
  '/connections': { title: 'Lightning Wallets', parent: '/settings' },
  '/connections/add': { title: 'Add Connection', parent: '/connections' },
  '/backup': { title: 'Backup', parent: '/settings' },
  '/bridge': { title: 'Convert', parent: '/' },
  '/wallets': { title: 'Wallets', parent: '/settings' },
  '/wallets/ecash': { title: 'Ecash Wallet', parent: '/wallets' },
};

/**
 * Match the current path against sub-page config.
 * Handles dynamic routes like /connections/:id.
 *
 * @param pathname - Current URL pathname
 * @returns Config object or null for top-level pages
 */
function getSubPageConfig(pathname: string): { title: string; parent: string } | null {
  // Exact match first
  const exact = SUB_PAGE_CONFIG[pathname];
  if (exact) return exact;

  // Dynamic route: /connections/:id (but not /connections/add)
  if (/^\/connections\/[^/]+$/.test(pathname) && pathname !== '/connections/add') {
    return { title: 'Connection Details', parent: '/connections' };
  }

  // Dynamic route: /contacts/:id
  if (/^\/contacts\/[^/]+$/.test(pathname)) {
    return { title: 'Contact', parent: '/contacts' };
  }

  // Dynamic route: /wallets/lightning/:id
  if (/^\/wallets\/lightning\/[^/]+$/.test(pathname)) {
    return { title: 'Lightning Wallet', parent: '/wallets' };
  }

  // Dynamic route: /wallets/ecash/mints/:mintUrl
  if (/^\/wallets\/ecash\/mints\/[^/]+$/.test(pathname)) {
    return { title: 'Mint Details', parent: '/wallets/ecash' };
  }

  return null;
}

export function Header(): React.ReactElement {
  const location = useLocation();
  const navigate = useNavigate();
  const dynamicTitle = usePageTitle();
  const subPage = getSubPageConfig(location.pathname);

  // Hide header entirely on tab pages (they render their own titles)
  if (HIDDEN_HEADER_ROUTES.has(location.pathname)) {
    return <></>;
  }

  if (subPage) {
    return (
      <header className="flex h-14 items-center bg-background px-4">
        <button
          type="button"
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors duration-fast"
          onClick={() => void navigate(subPage.parent)}
          aria-label="Go back"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="m15 18-6-6 6-6" />
          </svg>
          Back
        </button>
        <span className="flex-1 text-center text-lg font-semibold tracking-tight text-foreground">
          {dynamicTitle ?? subPage.title}
        </span>
        {/* Spacer to center the title */}
        <span className="w-14" aria-hidden="true" />
      </header>
    );
  }

  return (
    <header className="flex h-14 items-center justify-center bg-background px-4">
      <h1 className="text-lg font-semibold tracking-tight text-foreground">Satsflow</h1>
    </header>
  );
}
